import { ErrorConverter } from '../errorHandling/ErrorConverter';
import { CapturedApiError } from '../errorHandling/errorHandling.types';
import { CoreState, HttpResponse } from '../interfaces';
import { HttpErrorOrCapturedApiError } from './api.error.interfaces';
import { getIsApiConfigured } from './api.state';

/**
 * Given the response from a failed API request:
 * - convert a HTTP error to a "captured API" error
 * - return the standardized "API error" as the payload in the _rejected_ Redux action
 */
export function handleFailedApiResponse<ResponseType>(
  rejectWithValue: (value: CapturedApiError<ResponseType>) => any
) {
  return function returnStandardizedApiError(
    error: HttpErrorOrCapturedApiError<ResponseType>
  ) {
    const rejectedValue = ErrorConverter.isRawHttpError(error)
      ? ErrorConverter.fromHttpError<ResponseType>(error)
      : error;

    // Return the standardized "API error" as the payload in the _rejected_ Redux action
    return rejectWithValue(rejectedValue);
  };
}

/**
 * Given the response from a successful API request:
 * - determine if it contains an API fault or warning
 * - convert the response to a "captured API" error if so, then explicitly reject the promise
 * - return the response body as the payload in the _fulfilled_ Redux action otherwise
 */
export function handleCoreServicesFaultResponse<ResponseType>(
  response: HttpResponse<ResponseType>
) {
  // Reject the promise with the response as an error so it's handled like other (e.g. HTTP) errors
  if (ErrorConverter.isSuccessResponseWithFault(response)) {
    const apiFaultError = ErrorConverter.fromFaultResponse(response);

    return Promise.reject(apiFaultError);
  }

  // Resolve the promise with the response body as the payload in the fulfilled Redux action
  return Promise.resolve(response.data as ResponseType);
}

/** Verify the API is configured before executing a thunk. */
export function verifyApiIsConfigured<RequestDataType>(
  _data: RequestDataType,
  {
    getState
  }: {
    getState: () => unknown;
  }
): boolean {
  return getIsApiConfigured(getState() as CoreState);
}
